import React, {useEffect, useState} from "react";
import {connect, useSelector} from "react-redux";
import DocStatusTag from "../common/DocStatusTag";
import Warning from "../common/Warning";
import {useSubmitGuard, numberWithCommas, formatDateTimeToZone} from "../common/Helpers";
import {getQuote, editQuote, sendQuote, respondToQuote, deleteQuote, clearQuote} from "../../actions/quote";
import QuoteConvertModal from "./QuoteConvertModal";
import {BRAND_NAME} from "../../brand";

// Quote detail/response screen -- see GitHub #624/#632/#633 for the full design. Styled to
// match the existing Contract detail page (WorkflowSingle.js: .contract-card/.single-header/
// .contract-body/.doc-section, the .info-grid label+value pattern from WorkflowDetailsInfo.js,
// and the shared Warning banner) rather than inventing new page chrome. A Quote is project-less
// and has no ContractRole cast, so this doesn't reuse WorkflowSingle.js's party/workflow
// machinery -- it's a small, self-contained state machine over contract_status (Draft -> Pending
// -> Accepted -> Converted, or Declined at any point after Pending).
export function QuoteSingle(props) {

    const quote = useSelector((state) => state.quote.quote);

    const [form, setForm] = useState(null);
    const [recipientEmail, setRecipientEmail] = useState("");
    const [revisionReason, setRevisionReason] = useState("");
    const [convertModalOpen, setConvertModalOpen] = useState(false);

    const {isSubmitting: isSaving, guardedSubmit: guardSave} = useSubmitGuard();
    const {isSubmitting: isSending, guardedSubmit: guardSend} = useSubmitGuard();
    const {isSubmitting: isResponding, guardedSubmit: guardRespond} = useSubmitGuard();
    const {isSubmitting: isDeleting, guardedSubmit: guardDelete} = useSubmitGuard();

    useEffect(() => {
        props.getQuote(props.match.params.id);
        return () => props.clearQuote();
    }, [props.match.params.id]);

    useEffect(() => {
        if (quote.quote_id) {
            setForm({
                name: quote.quote_name || "",
                description: quote.quote_description || "",
                contract_value: quote.quote_value || "",
                start_date: quote.start_date || "",
                completion_date: quote.completion_date || "",
            });
            setRecipientEmail(quote.fk_email_recipient || "");
        }
    }, [quote.quote_id]);

    if (!quote.quote_id || !form) return null;

    const isSender = quote.is_sender_company;
    const isRecipient = quote.is_recipient_company;
    const isDraftForSender = quote.contract_status === "Draft" && isSender;

    const handleFieldChange = (e) => setForm({...form, [e.target.name]: e.target.value});

    const handleSave = guardSave((e) => {
        e.preventDefault();
        return props.editQuote(quote.quote_id, form);
    });

    const handleSend = guardSend((e) => {
        e.preventDefault();
        return props.sendQuote(quote.quote_id, recipientEmail);
    });

    const handleRespond = guardRespond((response) => {
        return props.respondToQuote(quote.quote_id, response, response === "RequestRevision" ? revisionReason : null).then((res) => {
            //AcceptAndConvert (GitHub #663) returns the new Contract's own response shape, not a
            //Quote's -- land the Purchaser straight on it, skipping the usual separate
            //accept-the-invitation step entirely
            if (res && res.data && !res.data.error && res.data.contract_id) {
                props.history.push(`/mng/workflow/view/${res.data.contract_id}`);
            }
        });
    });

    const handleDelete = guardDelete(() => {
        return props.deleteQuote(quote.quote_id).then(() => props.history.push("/quotes/list"));
    });

    // The sender is always the Vendor (enforced server-side at creation, see QuoteViewSet.create)
    // and their own fk_company_vendor is known immediately, while fk_company_purchaser stays null
    // until the recipient accepts -- so the counterparty is always the Purchaser side.
    const counterpartyCompany = quote.fk_company_purchaser;
    const counterpartyLabel = isRecipient
        ? (quote.fk_company_initiated_by.name || "Sender")
        : (counterpartyCompany?.name || quote.fk_email_recipient || "Recipient");

    // Shared between the Pending (Vendor can convert without waiting for a response, see
    // GitHub #624/#632/#633) and Accepted sections -- same control either way.
    const convertControl = quote.viewer_company_is_basic ? (
        <>
            <button className="btn pri min-width" onClick={() => setConvertModalOpen(true)}>Convert to Contract</button>
            {convertModalOpen && <QuoteConvertModal quoteId={quote.quote_id} setModalOpen={setConvertModalOpen} />}
        </>
    ) : (
        <button className="btn sec min-width disabled" disabled title="Coming soon">Convert to Contract &mdash; coming soon</button>
    );

    return (
        <div className="page-wrapper">
        <div className="contract-card gap-above-m">
            <div className="single-header">
                <h1 className="title heavy">Quote <span className="light-black">{quote.quote_name || "Untitled Quote"}</span></h1>
                <DocStatusTag status={quote.contract_status === "Converted" ? "Accepted" : quote.contract_status} fontSize="content2" />
            </div>

            <div className="contract-body">

                {quote.revision_request_reason && isDraftForSender &&
                    <Warning className="gap-below-m" type="info">
                        <span className="heavy">Revision requested: </span>{quote.revision_request_reason}
                    </Warning>}

                {quote.contract_status === "Declined" &&
                    <Warning className="gap-below-m">This Quote was declined.</Warning>}

                {isDraftForSender && (
                    <div className="doc-section gap-below-m">
                        <h3 className="title2 heavy label">{quote.revision_request_reason ? "Revise & Resend" : "Compose"}</h3>
                        <form onSubmit={handleSave} className="form">
                            <div className="inputs-wrapper gap-below-m">
                                <div className="input-basic">
                                    <input type="text" name="name" id="quote_edit_name" value={form.name} onChange={handleFieldChange} required />
                                    <label htmlFor="quote_edit_name">Quote Name <span>*</span></label>
                                </div>
                                <div className="input-basic">
                                    <input type="number" step="0.01" name="contract_value" id="quote_edit_value" value={form.contract_value} onChange={handleFieldChange} />
                                    <label htmlFor="quote_edit_value">Estimated Value</label>
                                </div>
                                <div className="input-basic">
                                    <input type="date" name="start_date" id="quote_edit_start" value={form.start_date} onChange={handleFieldChange} />
                                    <label htmlFor="quote_edit_start">Proposed Start Date</label>
                                </div>
                                <div className="input-basic">
                                    <input type="date" name="completion_date" id="quote_edit_completion" value={form.completion_date} onChange={handleFieldChange} />
                                    <label htmlFor="quote_edit_completion">Proposed Completion Date</label>
                                </div>
                                <div className="input-basic textarea addspace">
                                    <label className="textarea" htmlFor="quote_edit_description">Description</label>
                                    <textarea name="description" id="quote_edit_description" rows="3" value={form.description} onChange={handleFieldChange} />
                                </div>
                            </div>
                            <button className="btn sec min-width gap-below-m" type="submit" disabled={isSaving}>{isSaving ? "Saving..." : "Save Changes"}</button>

                            <div className="inputs-wrapper gap-below-s">
                                <div className="input-basic">
                                    <input type="email" id="quote_edit_recipient" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} required />
                                    <label htmlFor="quote_edit_recipient">Recipient Email <span>*</span></label>
                                </div>
                            </div>
                            <div className="flex-row">
                                <button className="btn pri min-width gap-right-m" onClick={handleSend} disabled={isSending || !recipientEmail}>{isSending ? "Sending..." : "Send Quote"}</button>
                                <button className="btn tb min-width" onClick={handleDelete} disabled={isDeleting}>{isDeleting ? "Deleting..." : "Delete"}</button>
                            </div>
                        </form>
                    </div>
                )}

                {quote.contract_status === "Pending" && isRecipient && (
                    <div className="doc-section gap-below-m">
                        <h3 className="title2 heavy label">Action Required</h3>
                        {/* GitHub #663: the Vendor pre-consented to one-step conversion at Quote-send time --
                            offer it alongside plain Accept rather than replacing it, so the Purchaser still has
                            the option to accept now and let the Vendor finish/share the Contract manually later */}
                        {quote.allow_contract_conversion &&
                        <div className="flex-row gap-below-m">
                            <button id="quote_accept_and_convert" className="btn pri min-width gap-right-m" onClick={() => handleRespond("AcceptAndConvert")} disabled={isResponding}>Accept &amp; Convert to Contract</button>
                        </div>}
                        <div className="flex-row gap-below-m">
                            <button id="quote_accept" className={quote.allow_contract_conversion ? "btn sec min-width gap-right-m" : "btn pri min-width gap-right-m"} onClick={() => handleRespond("Accept")} disabled={isResponding}>Accept</button>
                            <button id="quote_decline" className="btn sec min-width" onClick={() => handleRespond("Decline")} disabled={isResponding}>Decline</button>
                        </div>
                        <div className="inputs-wrapper gap-below-xs">
                            <div className="input-basic textarea addspace">
                                <label className="textarea" htmlFor="quote_revision_reason">Request a revision (optional message)</label>
                                <textarea id="quote_revision_reason" rows="3" value={revisionReason} onChange={(e) => setRevisionReason(e.target.value)} />
                            </div>
                        </div>
                        <button className="btn ter min-width" onClick={() => handleRespond("RequestRevision")} disabled={isResponding || !revisionReason}>Request Revision</button>
                    </div>
                )}

                {quote.contract_status === "Pending" && quote.is_vendor_company && (
                    <div className="doc-section gap-below-m">
                        <h3 className="title2 heavy label">Status</h3>
                        <p className="content1 light-black gap-below-m">
                            Waiting on a response from {quote.fk_email_recipient}. They may not have (or use) a
                            {" "}{BRAND_NAME} account &mdash; you don't have to wait for them to respond on the
                            platform to proceed.
                        </p>
                        <div className="flex-row">
                            {convertControl}
                            <button className="btn tb min-width gap-left-m" onClick={handleDelete} disabled={isDeleting}>{isDeleting ? "Deleting..." : "Delete Quote"}</button>
                        </div>
                    </div>
                )}

                {quote.contract_status === "Accepted" && (
                    <div className="doc-section gap-below-m">
                        <h3 className="title2 heavy label">Action Required</h3>
                        {quote.is_vendor_company ? convertControl : (
                            //only the Vendor can convert -- they're the one committing to
                            //actually perform the work, so they decide when to formalize it
                            <p className="content1 light-black">Waiting on the Vendor to convert this Quote.</p>
                        )}
                    </div>
                )}

                {/* the resulting Contract is private to the Vendor until they choose to share
                it (see convert()), so only the Vendor gets a working link to it here -- the
                Purchaser's top-of-page "Accepted" status tag is all they see */}
                {quote.contract_status === "Converted" && quote.fk_converted_contract_id && quote.is_vendor_company && (
                    <div className="doc-section gap-below-m">
                        <h3 className="title2 heavy label">Converted</h3>
                        <button className="btn sec min-width" onClick={() => props.history.push(`/mng/workflow/view/${quote.fk_converted_contract_id}`)}>View Contract</button>
                    </div>
                )}

                {!isDraftForSender && (
                    <div className="doc-section gap-below-m">
                        <h3 className="title2 heavy label">Details</h3>
                        <div className="info-grid c-3">
                            <div className="item firstcol">
                                <h3 className="content2 heavy-grey">Description</h3>
                                <h4 className="content1 light-black" style={{whiteSpace: "break-spaces"}}>{quote.quote_description || "-"}</h4>
                            </div>
                            <div className="item">
                                <h3 className="content2 heavy-grey">{isRecipient ? "Sender" : "Recipient"}</h3>
                                <h4 className="content1 light-black">{counterpartyLabel}</h4>
                            </div>
                            <div className="item">
                                <h3 className="content2 heavy-grey">Sent</h3>
                                <h4 className="content1 light-black">{quote.pending_date ? formatDateTimeToZone(quote.pending_date) : "-"}</h4>
                            </div>
                            <div className="item firstcol lastrow">
                                <h3 className="content2 heavy-grey">Value</h3>
                                <h4 className="content1 light-black">{quote.quote_value ? "$ " + numberWithCommas(quote.quote_value) : "-"}</h4>
                            </div>
                            <div className="item lastrow">
                                <h3 className="content2 heavy-grey">Proposed Start</h3>
                                <h4 className="content1 light-black">{quote.start_date || "-"}</h4>
                            </div>
                            <div className="item lastrow">
                                <h3 className="content2 heavy-grey">Proposed Completion</h3>
                                <h4 className="content1 light-black">{quote.completion_date || "-"}</h4>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </div>
    );
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps, {getQuote, editQuote, sendQuote, respondToQuote, deleteQuote, clearQuote})(QuoteSingle);
